import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronDown, Loader2 } from 'lucide-react';
import { CreateWebhookRequest, Webhook, AuthType } from '@/services/webhookService';

const EVENT_GROUPS = {
  'Order Lifecycle': [
    'PRESCRIPTION_CREATED',
    'PRESCRIPTION_VERIFIED',
    'PRESCRIPTION_SENT_TO_PHARMACY',
    'PRESCRIPTION_ACKNOWLEDGED',
    'PRESCRIPTION_FILLED',
    'PRESCRIPTION_READY',
    'PRESCRIPTION_SHIPPED',
    'PRESCRIPTION_DELIVERED',
    'PRESCRIPTION_CANCELLED',
  ],
  'Order Status': [
    'PRESCRIPTION_STATUS_CHANGED', // Catch-all for any status change
  ],
  'Encounters & Appointments': [
    'ENCOUNTER_COMPLETED',
    'APPOINTMENT_CREATED',
  ],
  'Fulfillment': [
    'rx.fulfilled',
    'rx.payment_confirmed',
    'shipment.submitted',
    'SHIPMENT_STATUS_CHANGED',
    'SHIPMENT_CANCELLED',
  ],
  'Patients': [
    'PATIENT_CREATED',
  ],
  'MDI (Telehealth)': [
    'MDI_CASE_SUBMITTED',
    'MDI_CASE_COMPLETED',
    'MDI_CASE_CANCELLED',
    'MDI_CASE_STATUS_CHANGED',
    'MDI_CASE_ASSIGNED',
    'MDI_MESSAGE_CREATED',
  ],
} as const;

// Flatten all events for validation and "select all" functionality
const AVAILABLE_EVENTS = Object.values(EVENT_GROUPS).flat() as readonly string[];

const webhookFormSchema = z
  .object({
    name: z.string().max(100, 'Name must be at most 100 characters').optional(),
    description: z.string().optional(),
    webhook_url: z
      .string()
      .min(1, 'Webhook URL is required')
      .url('Must be a valid URL')
      .max(500, 'URL must be at most 500 characters'),
    auth_type: z.enum(['none', 'basic', 'api_key', 'bearer']).default('basic'),
    api_key: z.string().max(500).optional(),
    bearer_token: z.string().optional(),
    webhook_username: z.string().max(500).optional(),
    webhook_password: z.string().max(500).optional(),
    subscribed_events: z.array(z.string()).default([]),
    enabled: z.boolean().default(true),
    timeout_ms: z
      .number()
      .min(1000, 'Timeout must be at least 1000ms')
      .max(30000, 'Timeout must be at most 30000ms')
      .default(5000),
    retry_config: z.object({
      enabled: z.boolean().default(true),
      max_attempts: z
        .number()
        .min(1, 'Max attempts must be at least 1')
        .max(10, 'Max attempts must be at most 10')
        .default(5),
      backoff_multiplier: z
        .number()
        .min(1, 'Backoff multiplier must be at least 1')
        .max(10, 'Backoff multiplier must be at most 10')
        .default(2),
    }),
    custom_headers: z.record(z.string()).optional(),
  })
  .refine(
    (data) => {
      if (data.auth_type === 'api_key') {
        return !!data.api_key && data.api_key.length > 0;
      }
      return true;
    },
    {
      message: 'API key is required when auth type is API Key',
      path: ['api_key'],
    }
  )
  .refine(
    (data) => {
      if (data.auth_type === 'bearer') {
        return !!data.bearer_token && data.bearer_token.length > 0;
      }
      return true;
    },
    {
      message: 'Bearer token is required when auth type is Bearer',
      path: ['bearer_token'],
    }
  )
  .refine(
    (data) => {
      if (data.auth_type === 'basic') {
        return (
          !!data.webhook_username &&
          data.webhook_username.length > 0 &&
          !!data.webhook_password &&
          data.webhook_password.length > 0
        );
      }
      return true;
    },
    {
      message: 'Username and password are required when auth type is Basic',
      path: ['webhook_username'],
    }
  );

type WebhookFormData = z.infer<typeof webhookFormSchema>;

interface WebhookFormProps {
  webhook?: Webhook | null;
  onSubmit: (data: CreateWebhookRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const WebhookForm: React.FC<WebhookFormProps> = ({
  webhook,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [advancedOpen, setAdvancedOpen] = React.useState(false);

  const form = useForm<WebhookFormData>({
    resolver: zodResolver(webhookFormSchema),
    defaultValues: {
      name: webhook?.name || '',
      description: webhook?.description || '',
      webhook_url: webhook?.webhook_url || '',
      auth_type: webhook?.auth_type || 'basic',
      api_key: '',
      bearer_token: '',
      webhook_username: '',
      webhook_password: '',
      subscribed_events: webhook?.subscribed_events || [],
      enabled: webhook?.enabled ?? true,
      timeout_ms: webhook?.timeout_ms || 5000,
      retry_config: webhook?.retry_config || {
        enabled: true,
        max_attempts: 5,
        backoff_multiplier: 2,
      },
      custom_headers: webhook?.custom_headers || {},
    },
  });

  const authType = form.watch('auth_type');
  const subscribedEvents = form.watch('subscribed_events');

  const handleSubmit = async (data: WebhookFormData) => {
    const submitData: CreateWebhookRequest = {
      name: data.name || undefined,
      description: data.description || undefined,
      webhook_url: data.webhook_url,
      auth_type: data.auth_type,
      subscribed_events: data.subscribed_events.length > 0 ? data.subscribed_events : [],
      enabled: data.enabled,
      timeout_ms: data.timeout_ms,
      retry_config: data.retry_config,
      custom_headers: data.custom_headers,
    };

    // Only include auth credentials if they're provided (for updates, empty string means don't update)
    if (data.auth_type === 'api_key' && data.api_key) {
      submitData.api_key = data.api_key;
    }
    if (data.auth_type === 'bearer' && data.bearer_token) {
      submitData.bearer_token = data.bearer_token;
    }
    if (data.auth_type === 'basic') {
      if (data.webhook_username) submitData.webhook_username = data.webhook_username;
      if (data.webhook_password) submitData.webhook_password = data.webhook_password;
    }

    await onSubmit(submitData);
  };

  const toggleEvent = (event: string) => {
    const current = subscribedEvents || [];
    if (current.includes(event)) {
      form.setValue('subscribed_events', current.filter((e) => e !== event));
    } else {
      form.setValue('subscribed_events', [...current, event]);
    }
  };

  const toggleAllEvents = () => {
    if (subscribedEvents.length === AVAILABLE_EVENTS.length) {
      form.setValue('subscribed_events', []);
    } else {
      form.setValue('subscribed_events', [...AVAILABLE_EVENTS]);
    }
  };

  const isGroupSelected = (groupEvents: readonly string[]): boolean => {
    return groupEvents.every(event => subscribedEvents.includes(event));
  };

  const isGroupPartiallySelected = (groupEvents: readonly string[]): boolean => {
    const selectedCount = groupEvents.filter(event => subscribedEvents.includes(event)).length;
    return selectedCount > 0 && selectedCount < groupEvents.length;
  };

  const toggleGroupEvents = (groupEvents: readonly string[]) => {
    const allSelected = groupEvents.every(event => subscribedEvents.includes(event));
    if (allSelected) {
      // Deselect all events in this group
      form.setValue(
        'subscribed_events',
        subscribedEvents.filter(event => !groupEvents.includes(event))
      );
    } else {
      // Select all events in this group
      const newEvents = [...subscribedEvents];
      groupEvents.forEach(event => {
        if (!newEvents.includes(event)) {
          newEvents.push(event);
        }
      });
      form.setValue('subscribed_events', newEvents);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-900">Basic Information</h3>

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name (Optional)</FormLabel>
                <FormControl>
                  <Input
                    placeholder="My Integration Webhook"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  A friendly name to identify this webhook
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description (Optional)</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Description of this webhook integration"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="webhook_url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Webhook URL *</FormLabel>
                <FormControl>
                  <Input
                    type="url"
                    placeholder="https://example.com/webhooks/novamed"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  The URL where webhook events will be sent
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Authentication */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-900">Authentication</h3>

          <FormField
            control={form.control}
            name="auth_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Authentication Type</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select authentication type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="basic">Basic Auth</SelectItem>
                    <SelectItem value="api_key">API Key</SelectItem>
                    <SelectItem value="bearer">Bearer Token</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {authType === 'api_key' && (
            <FormField
              control={form.control}
              name="api_key"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>API Key *</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="Enter API key" {...field} />
                  </FormControl>
                  <FormDescription>
                    Leave empty when editing to keep existing value
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {authType === 'bearer' && (
            <FormField
              control={form.control}
              name="bearer_token"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bearer Token *</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="Enter bearer token" {...field} />
                  </FormControl>
                  <FormDescription>
                    Leave empty when editing to keep existing value
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {authType === 'basic' && (
            <>
              <FormField
                control={form.control}
                name="webhook_username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Username *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter username" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="webhook_password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password *</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="Enter password" {...field} />
                    </FormControl>
                    <FormDescription>
                      Leave empty when editing to keep existing value
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
        </div>

        {/* Event Subscriptions */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-900">Event Subscriptions</h3>
          <FormDescription className="text-sm text-slate-600">
            Select events to subscribe to. Leave empty to subscribe to all events.
          </FormDescription>

          <div className="space-y-4">
            <div className="flex items-center space-x-2 pb-2 border-b">
              <Checkbox
                checked={subscribedEvents.length === AVAILABLE_EVENTS.length}
                onCheckedChange={toggleAllEvents}
              />
              <label className="text-sm font-medium cursor-pointer">
                Select All Events
              </label>
            </div>

            {Object.entries(EVENT_GROUPS).map(([groupName, groupEvents]) => (
              <div key={groupName} className="space-y-2 border rounded-lg p-3">
                <div className="flex items-center space-x-2 pb-2 border-b">
                  <Checkbox
                    checked={isGroupSelected(groupEvents)}
                    ref={(el) => {
                      if (el) {
                        el.indeterminate = isGroupPartiallySelected(groupEvents);
                      }
                    }}
                    onCheckedChange={() => toggleGroupEvents(groupEvents)}
                  />
                  <label className="text-sm font-semibold cursor-pointer text-slate-900">
                    {groupName}
                  </label>
                </div>
                <div className="pl-6 space-y-2">
                  {groupEvents.map((event) => (
                    <div key={event} className="flex items-center space-x-2">
                      <Checkbox
                        checked={subscribedEvents?.includes(event) || false}
                        onCheckedChange={() => toggleEvent(event)}
                      />
                      <label className="text-sm cursor-pointer text-slate-700">{event}</label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Enabled Toggle */}
        <FormField
          control={form.control}
          name="enabled"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Enabled</FormLabel>
                <FormDescription>
                  Disable to temporarily stop sending webhook events
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        {/* Advanced Settings */}
        <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
          <CollapsibleTrigger className="flex items-center justify-between w-full p-4 border rounded-lg hover:bg-slate-50">
            <span className="text-sm font-semibold text-slate-900">
              Advanced Settings
            </span>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${
                advancedOpen ? 'transform rotate-180' : ''
              }`}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-4 pt-4">
            <FormField
              control={form.control}
              name="timeout_ms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Timeout (milliseconds)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1000}
                      max={30000}
                      step={1000}
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value, 10))}
                    />
                  </FormControl>
                  <FormDescription>
                    Request timeout in milliseconds (1000-30000)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-4 border rounded-lg p-4">
              <FormLabel>Retry Configuration</FormLabel>

              <FormField
                control={form.control}
                name="retry_config.enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between">
                    <FormLabel>Enable Retries</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {form.watch('retry_config.enabled') && (
                <>
                  <FormField
                    control={form.control}
                    name="retry_config.max_attempts"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max Attempts</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            max={10}
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseInt(e.target.value, 10))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="retry_config.backoff_multiplier"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Backoff Multiplier</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            max={10}
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseInt(e.target.value, 10))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {webhook ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              webhook ? 'Update Webhook' : 'Create Webhook'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default WebhookForm;

